/* eslint-disable boundaries/element-types */
import { Button, message, Spin } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { generateAssetModel } from 'features/assetModeling'
import { useRolodexEdit } from 'features/fde'
import { useState } from 'react'
import styled from 'styled-components'
import { GeckoAssetModelType, GeckoUnit, getAssetModelTypeName } from 'types'

const StyledDescription = styled.div`
  display: flex;
  padding-bottom: 15px;
  flex-direction: column;
  height: 200px;
  justify-content: space-around;
`

const StyledUnitTypeButtons = styled(Button)`
  width: 100%;
  height: 50px;
  justify-content: start;
  padding: 0 10px 0 10px;
  display: flex;
  margin-bottom: 10px;
`

const StyledSpinDiv = styled.div`
  display: flex;
  justify-content: center;
`

interface AddUnitRichAssetModelInterface {
  unit: GeckoUnit
  refreshData: () => void
  bypassRAMCreation?: (value: boolean) => void
  goToSelectComponent?: () => void
}

export const AddUnitRichAssetModel = ({
  unit,
  refreshData,
  bypassRAMCreation,
  goToSelectComponent,
}: AddUnitRichAssetModelInterface) => {
  const { rolodexPut } = useRolodexEdit()
  const { GECKO_ASSETS_URL } = useRuntimeVariables()
  const [messageApi, contextHolder] = message.useMessage()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const handleUnitTypeClick = (
    unit: GeckoUnit,
    assetModelType: GeckoAssetModelType
  ) => {
    setIsLoading(true)
    const newAssetModel = generateAssetModel(unit, assetModelType)
    rolodexPut([newAssetModel])
      .then(() => {
        refreshData()
        return messageApi.success(
          'Successfully created a new Rich Asset Model! Loading...'
        )
      })
      .then(() => {
        setIsLoading(false)
        if (bypassRAMCreation) bypassRAMCreation(false)
        if (goToSelectComponent) goToSelectComponent()
      })
      .catch(() => {
        messageApi.error(
          'Failed to create new Rich Asset Model. Please try again.'
        )
      })
  }

  return (
    <div>
      {contextHolder}
      <StyledDescription>
        <h4>Create a Rich Asset Model</h4>
        <p>
          Before you can edit components on this unit, we need to know what kind
          of unit this is. This allows us to create a rich asset model, and
          improve the user experience.
        </p>
        {!!bypassRAMCreation && (
          <p>
            <strong>Note:</strong> If you don&apos;t see an appropriate unit
            type below, please skip this step by following the instructions
            below the selection.
          </p>
        )}
      </StyledDescription>
      <div>
        <div>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.VerticalTank)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/tank.png`}
              height={'40px'}
              alt={'Icon image of a large vertical tank'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.VerticalTank)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.HorizontalTank)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/pressure vessel.png`}
              height={'40px'}
              alt={'Icon image of a horizontal tank'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.HorizontalTank)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.Piping)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/pipeline.png`}
              height={'40px'}
              alt={'Icon image of a long pipe'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.Piping)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.LegacyBoiler)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/boiler.png`}
              height={'40px'}
              alt={'Icon image of a power boiler'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.LegacyBoiler)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() => handleUnitTypeClick(unit, GeckoAssetModelType.Ship)}
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/boat.png`}
              height={'40px'}
              alt={'Icon image of a large ship'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.Ship)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.MissileSilo)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/silo.png`}
              height={'40px'}
              alt={'Icon image of a missile silo'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.MissileSilo)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(
                unit,
                GeckoAssetModelType.NuclearContainmentDome
              )
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/containment dome.png`}
              height={'40px'}
              alt={'Icon image of a nuclear containment dome'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.NuclearContainmentDome)}
          </StyledUnitTypeButtons>
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.MachinedForging)
            }
            disabled={isLoading}>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/machined forging.png`}
              height={'40px'}
              alt={'Icon image of a machined forging'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.MachinedForging)}
          </StyledUnitTypeButtons>
          {/* TODO: Uncomment once pressure vessel is fully implemented */}
          <StyledUnitTypeButtons
            onClick={() =>
              handleUnitTypeClick(unit, GeckoAssetModelType.PressureVessel)
            }>
            <img
              src={`${GECKO_ASSETS_URL}/rich_asset_model/pressure vessel.png`}
              height={'40px'}
              alt={'Icon image of a pressure vessel'}
            />
            {getAssetModelTypeName(GeckoAssetModelType.PressureVessel)}
          </StyledUnitTypeButtons>
        </div>
        {!!bypassRAMCreation && (
          <p>
            Not ready to make a Rich Asset Model for this Unit?{' '}
            <a
              onClick={() => {
                if (bypassRAMCreation) bypassRAMCreation(true)
                if (goToSelectComponent) goToSelectComponent()
              }}>
              {' '}
              Edit the components directly here
            </a>
          </p>
        )}
      </div>
      <StyledSpinDiv>
        <Spin spinning={isLoading} />
      </StyledSpinDiv>
    </div>
  )
}
